import { useState } from 'react';
import { Title } from 'react-admin';
import { useQuery } from '@apollo/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Skeleton from '@mui/material/Skeleton';
import Chip from '@mui/material/Chip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Grid from '@mui/material/Grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import ClearIcon from '@mui/icons-material/Clear';

import { GET_CONFIG_AUDIT_LOGS } from './payment.queries';
import { formatDate } from './payment.types';

interface AuditLog {
  id: string;
  action: string;
  entity: string;
  entityId: string;
  userId: string;
  userEmail: string;
  previousValue: any;
  newValue: any;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

// Entity labels
const ENTITY_LABELS: Record<string, string> = {
  provider_config: 'Configuration Provider',
  hybrid_limit: 'Limite Hybride',
  country_routing: 'Routing Pays',
};

// Action colors
const ACTION_COLORS: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
  CREATE: 'success',
  UPDATE: 'warning',
  DELETE: 'error',
};

// Action labels
const ACTION_LABELS: Record<string, string> = {
  CREATE: 'Création',
  UPDATE: 'Modification',
  DELETE: 'Suppression',
};

interface DetailDialogProps {
  log: AuditLog | null;
  open: boolean;
  onClose: () => void;
}

function DetailDialog({ log, open, onClose }: DetailDialogProps) {
  if (!log) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon />
          Détails de l'audit
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Basic info */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Informations générales
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Action</Typography>
                <Chip
                  label={ACTION_LABELS[log.action] || log.action}
                  size="small"
                  color={ACTION_COLORS[log.action] || 'default'}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Entité</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {ENTITY_LABELS[log.entity] || log.entity}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">ID Entité</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {log.entityId}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Date</Typography>
                <Typography variant="body2">{formatDate(log.createdAt)}</Typography>
              </Box>
            </Box>
          </Grid>

          {/* User info */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Utilisateur
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Email</Typography>
                <Typography variant="body2">{log.userEmail}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">ID Utilisateur</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {log.userId}
                </Typography>
              </Box>
              {log.ipAddress && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Adresse IP</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {log.ipAddress}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Previous value */}
          {log.previousValue && (
            <Grid item xs={12}>
              <Accordion defaultExpanded={log.action === 'UPDATE'}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Valeur précédente</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    component="pre"
                    sx={{
                      backgroundColor: '#FEE2E2',
                      p: 2,
                      borderRadius: 1,
                      overflow: 'auto',
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      maxHeight: 300,
                    }}
                  >
                    {JSON.stringify(log.previousValue, null, 2)}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}

          {/* New value */}
          {log.newValue && (
            <Grid item xs={12}>
              <Accordion defaultExpanded={log.action !== 'DELETE'}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Nouvelle valeur</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    component="pre"
                    sx={{
                      backgroundColor: '#D1FAE5',
                      p: 2,
                      borderRadius: 1,
                      overflow: 'auto',
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      maxHeight: 300,
                    }}
                  >
                    {JSON.stringify(log.newValue, null, 2)}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}

          {/* User agent */}
          {log.userAgent && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                User Agent
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  wordBreak: 'break-all',
                }}
              >
                {log.userAgent}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * AuditLogViewer Component
 * Displays payment configuration audit logs with filtering
 */
export function AuditLogViewer() {
  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Filter state
  const [filters, setFilters] = useState<{
    entity?: string;
    action?: string;
    userId?: string;
  }>({});
  const [showFilters, setShowFilters] = useState(false);

  // Dialog state
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  // Query
  const { data, loading, refetch } = useQuery<{
    paymentConfigAuditLogs: { data: AuditLog[]; total: number };
  }>(GET_CONFIG_AUDIT_LOGS, {
    variables: {
      filter: {
        ...filters,
        limit: pageSize,
        offset: page * pageSize,
      },
    },
  });

  const logs = data?.paymentConfigAuditLogs.data || [];
  const total = data?.paymentConfigAuditLogs.total || 0;

  const handleClearFilters = () => {
    setFilters({});
    setPage(0);
  };

  return (
    <Box>
      <Title title="Logs d'audit" />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Historique des modifications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Traçabilité complète des modifications de configuration
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Filtres">
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterListIcon color={Object.keys(filters).length > 0 ? 'primary' : 'inherit'} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Actualiser">
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      {showFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Entité</InputLabel>
                <Select
                  value={filters.entity || ''}
                  label="Entité"
                  onChange={(e) => {
                    setFilters({ ...filters, entity: e.target.value || undefined });
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Toutes</MenuItem>
                  {Object.entries(ENTITY_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Action</InputLabel>
                <Select
                  value={filters.action || ''}
                  label="Action"
                  onChange={(e) => {
                    setFilters({ ...filters, action: e.target.value || undefined });
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Toutes</MenuItem>
                  {Object.entries(ACTION_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                label="Utilisateur (email)"
                value={filters.userId || ''}
                onChange={(e) => {
                  setFilters({ ...filters, userId: e.target.value || undefined });
                  setPage(0);
                }}
                sx={{ minWidth: 200 }}
              />

              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                size="small"
              >
                Effacer
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Entité</TableCell>
                <TableCell>Utilisateur</TableCell>
                <TableCell>Résumé</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="circular" width={32} height={32} />
                    </TableCell>
                  </TableRow>
                ))
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography color="text.secondary">Aucun log trouvé</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Typography variant="body2">{formatDate(log.createdAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ACTION_LABELS[log.action] || log.action}
                        size="small"
                        color={ACTION_COLORS[log.action] || 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {ENTITY_LABELS[log.entity] || log.entity}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {log.entityId.substring(0, 8)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{log.userEmail}</Typography>
                      {log.ipAddress && (
                        <Typography variant="caption" color="text.secondary">
                          {log.ipAddress}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {log.action === 'CREATE' && 'Nouvelle entrée créée'}
                        {log.action === 'UPDATE' && 'Configuration mise à jour'}
                        {log.action === 'DELETE' && 'Entrée supprimée'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Voir les détails">
                        <IconButton size="small" onClick={() => setSelectedLog(log)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Par page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
      </Card>

      {/* Detail dialog */}
      <DetailDialog log={selectedLog} open={Boolean(selectedLog)} onClose={() => setSelectedLog(null)} />
    </Box>
  );
}

export default AuditLogViewer;
